import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FC, ReactNode } from 'react';
import { v4 as uuid } from 'uuid';
import {
  isPayAsBidTwoBiddingRounds,
  storageCommodityUnitLabels,
  storageCommodityUnitValues,
  storageFlowUnitLabels,
  storageOfferStatusLabels,
  storagePriceTimeUnitLabels,
} from 'src/apis/storage/mappings';
import type {
  StorageAllocationUnit,
  StoragePriceQuantityUnit,
  StorageCommodityUnit,
  StorageFlowUnit,
  StorageOfferDetails,
  StorageOffersOverviewParams,
  StorageOfferStatus,
  StorageProductType,
  StorageRound,
  StorageTypeOfProduct,
} from 'src/apis/storage/types';
import {
  storageServiceRootCacheKey,
  type StorageServiceRootRequest,
} from 'src/apis/storage/use-storage-service-root';
import { EmptyValue } from 'src/components/data-display/empty-value';
import { FormatDate } from 'src/components/data-display/format-date';
import {
  FormatValue,
  type NumberType,
} from 'src/components/data-display/format-value';
import {
  StatusDisplay,
  type StatusProps,
  useStatusDisplayMode,
} from 'src/components/data-display/status-display';
import type { FormOption } from 'src/components/form/form-option';
import { Tooltip } from 'src/components/overlay/tooltip';
import type { Successful } from 'src/hooks/use-axios';
import { useAxiosCache } from 'src/hooks/use-axios/axios-cache';
import { stringifySearchParams } from 'src/hooks/use-page-params-from-schema';
import { FormatTimerForTable, useTimer } from 'src/hooks/use-timer';
import type { ProductComponentSchema } from 'src/pages/storage/offers-new/create/schema';
import { cn } from 'src/utils/cn';
import type { Currency } from 'src/utils/currencies';

export const STORAGE_OFFER_CURRENCY: Currency = 'EUR';

const convertBiddingWindowDuration = (minutes: number) => {
  if (minutes < 60) {
    return `${minutes}min`;
  } else if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}min`;
  } else {
    const days = Math.floor(minutes / 1440);
    const remainingMinutes = minutes % 1440;
    const hours = Math.floor(remainingMinutes / 60);
    const mins = remainingMinutes % 60;
    return `${days}d ${hours > 0 ? `${hours}h` : ''} ${mins}min`;
  }
};

export const renderBiddingWindowFields = (
  offer: StorageOfferDetails
): ReactNode => {
  if (isPayAsBidTwoBiddingRounds(offer.allocationMethod)) {
    return (
      <>
        <span>First Round Bidding Window Start</span>
        <span data-testid="first-round-bidding-window-start">
          {offer.firstRoundBiddingWindowStart ? (
            <FormatDate
              value={offer.firstRoundBiddingWindowStart}
              type="date-time"
            />
          ) : (
            <EmptyValue />
          )}
        </span>
        <span>Bidding Window Duration</span>
        <span data-testid="bidding-window-duration">
          {renderBiddingWindowDuration(offer.biddingWindowDurationInMinutes)}
        </span>
        <span>Time Between Bidding Windows</span>
        <span data-testid="time-between-bidding-windows">
          {renderBiddingWindowDuration(
            offer.timeBetweenBiddingWindowsInMinutes
          )}
        </span>
      </>
    );
  }

  const biddingWindow = offer.rounds[0]?.biddingWindow;
  if (!biddingWindow) return null;

  return (
    <>
      <span>Bidding Window</span>
      <span data-testid="bidding-window">
        <FormatDate value={biddingWindow.start} type="date-time" />
        {' - '}
        <FormatDate value={biddingWindow.end} type="date-time" />
      </span>
    </>
  );
};

const renderBiddingWindowDuration = (
  minutes: number | null | undefined
): ReactNode => {
  if (minutes == null) return <EmptyValue />;
  return convertBiddingWindowDuration(minutes);
};

export const convertTimeInMinutesToHoursMinutes = (timeInMinutes: number) => {
  const hours = Math.floor(timeInMinutes / 60);
  const remainingMinutes = timeInMinutes % 60;
  const minutes = remainingMinutes % 60;
  return { hours, minutes };
};

export function getLegacyOffersSearch(ssoShortNames: string[]): string {
  return stringifySearchParams({
    tsoShortName: ssoShortNames.map((shortName) => ({
      label: shortName,
      value: shortName,
    })),
  });
}

export function getLegacyFinishedOffersSearch(ssoShortNames: string[]): string {
  return stringifySearchParams({
    quickFilter: 'FINISHED',
    tsoShortName: ssoShortNames.map((shortName) => ({
      label: shortName,
      value: shortName,
    })),
  });
}

/** Default query when loading the public offers list (e.g. create-offer duplicate checks). */
export const storageOffersListDefaultParams: StorageOffersOverviewParams = {
  limit: 100,
  offset: 0,
  sortBy: 'OFFER_BUSINESS_ID',
  sortDirection: 'asc',
};

/** True when `unit` is a commodity unit (m³, kWh, MWh, …) rather than a flow unit. */
function isCommodityUnit(unit: string): unit is StorageCommodityUnit {
  return (storageCommodityUnitValues as readonly string[]).includes(unit);
}

/** Display label for a bidding-unit option: a commodity unit (`MWh`) or a flow unit (`kWh/h`). */
function getStoragePriceQuantityUnitLabel(
  unit: StoragePriceQuantityUnit
): string {
  if (isCommodityUnit(unit)) return storageCommodityUnitLabels[unit];
  return storageFlowUnitLabels[unit];
}

export function getStorageProductQuantityUnitLabel(
  typeOfProduct: StorageTypeOfProduct
): string {
  switch (typeOfProduct.typeOfProduct) {
    case 'SBU':
      return 'SBU';
    case 'STORAGE_VOLUME':
      return storageCommodityUnitLabels[typeOfProduct.unit];
    case 'INJECTION_CAPACITY':
    case 'WITHDRAWAL_CAPACITY':
      return storageFlowUnitLabels[typeOfProduct.unit];
  }
}

/** Label for an allocated amount's unit, including SBU. */
export function getStorageAllocationUnitLabel(
  unit: StorageAllocationUnit
): string {
  if (unit === 'SBU') return 'SBU';
  if (isCommodityUnit(unit)) return storageCommodityUnitLabels[unit];
  return storageFlowUnitLabels[unit];
}

/**
 * Quantity unit shown on reserve and bid prices.
 * Uses the offer's bidding unit when one was chosen, otherwise the product quantity unit.
 */
function getStorageBiddingQuantityUnitLabel(
  offer: StorageOfferDetails
): string {
  if (offer.priceQuantityUnit)
    return getStorageAllocationUnitLabel(offer.priceQuantityUnit);
  return getStorageProductQuantityUnitLabel(offer.typeOfProduct);
}

/** Suffix after a reserve or bid amount: `/${quantity}/${time}` (for example `/MWh/d`). */
export function getStorageReservePriceSuffix(
  offer: StorageOfferDetails
): string {
  const unitLabel = getStorageBiddingQuantityUnitLabel(offer);
  return `/${unitLabel}/${storagePriceTimeUnitLabels[offer.priceTimeUnit]}`;
}

/** Full price unit, currency included (for example `EUR/MWh/d`). */
export function getStorageBiddingPriceUnit(offer: StorageOfferDetails): string {
  return `${STORAGE_OFFER_CURRENCY}${getStorageReservePriceSuffix(offer)}`;
}

/** Maps an API type of product onto the create/edit form's product type and unit. */
export function mapTypeOfProductToForm(typeOfProduct: StorageTypeOfProduct): {
  productType: StorageProductType;
  typeOfProductUnit: string;
} {
  switch (typeOfProduct.typeOfProduct) {
    case 'STORAGE_VOLUME':
      return {
        productType: 'storageVolume',
        typeOfProductUnit: typeOfProduct.unit,
      };
    case 'INJECTION_CAPACITY':
      return {
        productType: 'injectionCapacity',
        typeOfProductUnit: typeOfProduct.unit,
      };
    case 'WITHDRAWAL_CAPACITY':
      return {
        productType: 'withdrawalCapacity',
        typeOfProductUnit: typeOfProduct.unit,
      };
    case 'SBU':
      return { productType: 'SBU', typeOfProductUnit: typeOfProduct.unit };
  }
}

/**
 * Maps API product components onto form rows.
 * Amount and unit are flattened, and `id` is a client-only key (not sent to the API).
 */
export function mapProductComponentsFromDetails(
  productComponents: StorageOfferDetails['productComponents']
): ProductComponentSchema[] {
  return productComponents.map((component) => ({
    id: uuid(),
    capacityCategory: component.capacityCategory,
    runtimeStart: component.runtime.start,
    runtimeEnd: component.runtime.end,
    storageVolumeAmount: component.storageVolume?.amount ?? null,
    storageVolumeUnit: component.storageVolume?.unit ?? null,
    injectionCapacityAmount: component.injectionCapacity?.amount ?? null,
    injectionCapacityUnit: component.injectionCapacity?.unit ?? null,
    withdrawalCapacityAmount: component.withdrawalCapacity?.amount ?? null,
    withdrawalCapacityUnit: component.withdrawalCapacity?.unit ?? null,
  }));
}

const storageOfferStatusVariantMap: Record<
  StorageOfferStatus,
  StatusProps['variant']
> = {
  OPEN: 'ready',
  FINISHED: 'success',
  DELETED: 'inactive',
  PUBLISHED: 'warning',
  CREATED: 'warning',
  WAITING_FOR_NEXT_ROUND: 'warning',
  APPROVAL_PENDING: 'warning',
  UNSUCCESSFUL: 'error',
  CANCELLED: 'inactive',
  FAILED: 'error',
};

export function formatCommodityDisplay(
  value: { amount: number; unit: StorageCommodityUnit } | null | undefined
): ReactNode {
  if (!value) {
    return <EmptyValue />;
  }
  return (
    <>
      <FormatValue
        value={value.amount}
        label=""
        hideLabel
        type="exact-decimals"
      />{' '}
      {storageCommodityUnitLabels[value.unit]}
    </>
  );
}

export function formatCapacityDisplay(
  value: { amount: number; unit: StorageFlowUnit } | null | undefined
): ReactNode {
  if (!value) {
    return <EmptyValue />;
  }
  return (
    <>
      <FormatValue
        value={value.amount}
        label=""
        hideLabel
        type="exact-decimals"
      />{' '}
      {storageFlowUnitLabels[value.unit]}
    </>
  );
}

export const FormatValueRange: FC<{
  min: number;
  max: number;
  label: string;
  type?: NumberType;
}> = ({ min, max, label, type = 'exact-decimals' }) =>
  min === max ? (
    <FormatValue value={min} label={label} type={type} />
  ) : (
    <>
      <FormatValue value={min} label={label} type={type} />
      {' - '}
      <FormatValue value={max} label={label} type={type} />
    </>
  );

const Countdown: FC<{
  timestamp: string;
  storageServiceRoot: Successful<StorageServiceRootRequest>;
}> = ({ timestamp, storageServiceRoot }) => {
  const timer = useTimer(timestamp);
  const axiosCache = useAxiosCache();
  const mode = useStatusDisplayMode();

  if (!timer) return null;
  return (
    <div data-testid="storage-offer-countdown">
      <Tooltip
        content={<FormatDate value={timestamp} type="date-time" />}
        dataTestId="storage-offer-countdown-tooltip"
      >
        {(targetProps) => (
          <span
            {...targetProps}
            aria-label="Countdown exact time"
            className={cn(
              `
                relative
                after:absolute after:-right-2xs after:content-['…']
              `,
              mode === 'page' ? 'after:top-xs' : 'after:top-2xs',
              `
                after:text-label-sm after:font-label-semibold
                after:text-foreground-primary-inverted
              `
            )}
          >
            <FormatTimerForTable
              timer={timer}
              onElapsedRefresh={() => {
                axiosCache.invalidate(storageServiceRootCacheKey);
                storageServiceRoot.refresh?.();
              }}
            />
          </span>
        )}
      </Tooltip>
    </div>
  );
};

export const StorageOfferStatusWithTimer: FC<{
  status: StorageOfferStatus;
  storageServiceRoot: Successful<StorageServiceRootRequest>;
  cancellationReason?: string | null;
  countdownEnd?: string | null;
  'data-testid'?: string;
}> = ({
  status,
  storageServiceRoot,
  cancellationReason,
  countdownEnd,
  'data-testid': testId,
}) => {
  const displayTestId = testId ?? 'status-display';

  return (
    <div className="inline-grid grid-flow-col items-center justify-start gap-xs">
      <StatusDisplay
        variant={storageOfferStatusVariantMap[status]}
        status={storageOfferStatusLabels[status]}
        data-testid={displayTestId}
        appendix={
          countdownEnd && (
            <Countdown
              timestamp={countdownEnd}
              storageServiceRoot={storageServiceRoot}
            />
          )
        }
      />
      {cancellationReason ? (
        <Tooltip
          dataTestId={`${displayTestId}-cancellation-reason-tooltip`}
          content={cancellationReason}
        >
          {(targetProps) => (
            <FontAwesomeIcon {...targetProps} icon={faInfoCircle} />
          )}
        </Tooltip>
      ) : null}
    </div>
  );
};

/**
 * Rounds to show on the offer. Both stay visible until the offer is
 * waiting for the next round, when round 1 drops off. A round whose
 * window has ended is omitted, as is any round before the one currently open.
 */
export function findVisibleRounds(
  rounds: StorageRound[],
  offerStatus: StorageOfferStatus,
  now: number
): StorageRound[] {
  const openRound = rounds.find((round) => round.isOpen);

  return rounds
    .filter((round) => {
      if (offerStatus === 'WAITING_FOR_NEXT_ROUND' && round.roundNumber === 1) {
        return false;
      }

      if (openRound != null && round.roundNumber < openRound.roundNumber) {
        return false;
      }

      if (round.isOpen) return true;

      return new Date(round.biddingWindow.end).getTime() > now;
    })
    .toSorted((left, right) => left.roundNumber - right.roundNumber);
}

type BiddingUnitOption = FormOption<StoragePriceQuantityUnit> & {
  hint: string;
};

export function getBiddingUnitOptions(
  components: ProductComponentSchema[]
): BiddingUnitOption[] {
  const hintsByUnit = new Map<StoragePriceQuantityUnit, string[]>();
  const add = (
    unit: StoragePriceQuantityUnit | null | undefined,
    hint: string
  ) => {
    if (!unit) return;
    const hints = hintsByUnit.get(unit) ?? [];
    if (!hints.includes(hint)) hints.push(hint);
    hintsByUnit.set(unit, hints);
  };

  for (const component of components) {
    add(component.storageVolumeUnit, 'Storage Volume');
    add(component.injectionCapacityUnit, 'Injection Capacity');
    add(component.withdrawalCapacityUnit, 'Withdrawal Capacity');
  }

  return [...hintsByUnit].map(([unit, hints]) => ({
    value: unit,
    label: getStoragePriceQuantityUnitLabel(unit),
    hint: hints.join(', '),
  }));
}
